import React, { useState, useCallback } from 'react';

interface LogoProps {
  className?: string;
  alt?: string;
  style?: React.CSSProperties;
  onLoad?: () => void;
  onError?: () => void;
  loading?: 'eager' | 'lazy';
  decoding?: 'sync' | 'async' | 'auto';
}

const Logo: React.FC<LogoProps> = ({
  className,
  alt = 'Zoe Church Logo',
  style,
  onLoad,
  onError,
  loading = 'eager',
  decoding = 'sync'
}) => {
  const [currentSrc, setCurrentSrc] = useState('/zoe-logo.svg');
  const [hasError, setHasError] = useState(false);
  const [attemptCount, setAttemptCount] = useState(0);

  // Logo sources in order of preference
  const logoSources = React.useMemo(() => [
    '/zoe-logo.svg',
    '/logos/zoe-logo.svg', 
    'https://assets.zoeforallnations.com/logos/zoe-logo.svg',
    'https://assets.zoeforallnations.com/zoe-logo.svg'
  ], []);

  const handleLoad = useCallback(() => {
    setHasError(false);
    onLoad?.();
  }, [onLoad]);

  const handleError = useCallback(() => {
    const nextAttempt = attemptCount + 1;
    
    if (nextAttempt < logoSources.length) {
      setAttemptCount(nextAttempt);
      setCurrentSrc(logoSources[nextAttempt]);
    } else {
      setHasError(true);
      onError?.();
    }
  }, [attemptCount, logoSources, onError]);

  if (hasError) {
    return (
      <div className={`flex items-center justify-center ${className}`}>
        <div className="flex flex-col items-center space-y-2 text-white/60">
          <svg className="w-16 h-16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              strokeWidth={2} 
              d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" 
            />
          </svg>
          <div className="text-sm">ZOE</div>
        </div>
      </div>
    );
  }

  return (
    <img
      src={currentSrc}
      alt={alt}
      className={className}
      style={style}
      loading={loading}
      decoding={decoding}
      onLoad={handleLoad}
      onError={handleError}
    />
  );
};

export default Logo;